import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { LayoutGrid, Shirt, Layers, Sparkles, Copy, ShoppingBag, Truck, BadgeCheck, Package, Zap, Star, Crown, Gem, Tag, Gift } from "lucide-react";
import ProductCard from "@/components/kiyana/ProductCard";

const CAT_ICONS = {
  "layout-grid": LayoutGrid, shirt: Shirt, layers: Layers, sparkles: Sparkles,
  copy: Copy, "shopping-bag": ShoppingBag, star: Star, crown: Crown, gem: Gem, tag: Tag, gift: Gift,
};

function FlashCountdown({ endAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!endAt) return null;
  const diff = Math.max(0, new Date(endAt).getTime() - now);
  const h = String(Math.floor(diff / 3600000)).padStart(2, "0");
  const m = String(Math.floor(diff / 60000) % 60).padStart(2, "0");
  const s = String(Math.floor(diff / 1000) % 60).padStart(2, "0");
  return (
    <div className="flex items-center gap-0.5" data-testid="flash-countdown">
      {[h, m, s].map((v, i) => (
        <span key={i} className="flex items-center gap-0.5">
          <span className="bg-cocoa text-white text-[11px] font-bold rounded px-1.5 py-0.5 font-mono">{v}</span>
          {i < 2 && <span className="text-cocoa font-bold text-[11px]">:</span>}
        </span>
      ))}
    </div>
  );
}

const CATS = [
  { id: "all", name: "Semua Produk", icon: "layout-grid" },
  { id: "atasan", name: "Atasan", icon: "shirt" },
  { id: "bottom", name: "Bottom", icon: "layers" },
  { id: "dress", name: "Dress", icon: "sparkles" },
  { id: "setelan", name: "Setelan", icon: "copy" },
  { id: "aksesori", name: "Tas & Aksesoris", icon: "shopping-bag" },
];

export default function StoreHome() {
  const [products, setProducts] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [flash, setFlash] = useState(null);
  const [cats, setCats] = useState(CATS);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/vouchers").then((r) => setVouchers(r.data)).catch(() => {});
    api.get("/flashsale").then((r) => setFlash(r.data)).catch(() => {});
    api.get("/store/config").then((r) => { if (r.data?.store_cats?.length) setCats(r.data.store_cats); }).catch(() => {});
  }, []);

  const newest = products.slice(-6).reverse();
  const bestSeller = [...products].sort((a, b) => (b.sold_total ?? b.sold ?? 0) - (a.sold_total ?? a.sold ?? 0)).slice(0, 6);

  return (
    <div className="space-y-6 animate-fade-up">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#FBEBEE] to-[#F6DEE3]">
        <div className="grid grid-cols-5 items-center">
          <div className="col-span-3 p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-brand font-semibold">New Arrival</div>
            <h1 className="font-serif-fashion text-2xl sm:text-3xl leading-tight mt-1 text-cocoa">
              OOTD LUCU<br />TIAP HARI <span className="text-brand">♡</span>
            </h1>
            <p className="text-[11px] text-cocoa-muted mt-2 leading-relaxed">
              Tampil casual, manis, dan percaya diri bersama Kiyana by Cirebon Cloth
            </p>
            <Link
              to="/store/categories"
              data-testid="hero-shop-btn"
              className="inline-block mt-3 px-5 h-9 leading-9 rounded-full bg-brand text-white text-xs font-semibold"
            >
              Shop Now
            </Link>
          </div>
          <div className="col-span-2 h-44 sm:h-52">
            <img
              src="https://images.pexels.com/photos/8217881/pexels-photo-8217881.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Kiyana New Arrival"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </div>

      {/* Kategori ikon — bisa dikustomisasi di Pengaturan admin */}
      <div className="grid grid-cols-6 gap-1">
        {cats.map((c, i) => {
          const CatIcon = CAT_ICONS[c.icon] || LayoutGrid;
          return (
            <Link
              key={`${c.id}-${i}`}
              to={c.id === "all" ? "/store/categories" : `/store/categories?cat=${c.id}`}
              data-testid={`home-cat-${c.id}`}
              className="flex flex-col items-center gap-1.5"
            >
              <div className="w-11 h-11 rounded-xl bg-white border border-blush-soft flex items-center justify-center">
                <CatIcon className="w-5 h-5 text-brand" />
              </div>
              <div className="text-[9px] text-center leading-tight text-cocoa">{c.name}</div>
            </Link>
          );
        })}
      </div>

      {/* Flash Sale */}
      {flash && (
        <div data-testid="flashsale-section" className="rounded-2xl bg-gradient-to-br from-[#FCE9EC] to-[#F7DEE3] p-3.5 border border-blush-soft">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-brand fill-brand" />
            <span className="font-bold text-sm text-cocoa tracking-wide">FLASH SALE</span>
            <FlashCountdown endAt={flash.end_at} />
            <Link to="/store/categories" className="ml-auto text-[10px] text-brand font-medium">Lihat Semua ›</Link>
          </div>
          <div className="flex gap-2.5 overflow-x-auto no-scrollbar -mx-1 px-1">
            {flash.items.map((f) => (
              <Link key={f.id} to={`/store/product/${f.id}`} data-testid={`flash-${f.id}`} className="w-32 flex-shrink-0 bg-white rounded-xl overflow-hidden border border-blush-soft">
                <div className="relative aspect-square">
                  <img src={f.image} alt={f.name} className="w-full h-full object-cover" />
                  <span className="absolute top-0 right-0 bg-amber-400 text-amber-900 text-[10px] font-bold px-1.5 py-0.5 rounded-bl-lg">-{f.discount_percent}%</span>
                </div>
                <div className="p-2">
                  <div className="text-[9px] text-cocoa-muted line-through">{formatIDR(f.price)}</div>
                  <div className="text-brand font-bold text-sm">{formatIDR(f.flash_price)}</div>
                  <div className="mt-1.5 h-3.5 rounded-full bg-blush overflow-hidden relative">
                    <div className="h-full bg-gradient-to-r from-brand to-[#D98A97] rounded-full transition-all" style={{ width: `${Math.min(100, Math.round(f.sold / f.quota * 100))}%` }} />
                    <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-white drop-shadow">STOK TERBATAS</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Produk Terbaru */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-sm">Produk Terbaru</h2>
          <Link to="/store/categories" className="text-[11px] text-brand font-medium">Lihat semua ›</Link>
        </div>
        <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4">
          {newest.map((p) => <div key={p.id} className="w-40 flex-shrink-0"><ProductCard p={p} /></div>)}
        </div>
      </div>

      {/* Service badges */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { icon: Truck, t: "Gratis Ongkir", d: "Min. belanja 150K" },
          { icon: BadgeCheck, t: "Bahan Berkualitas", d: "Nyaman dipakai" },
          { icon: Package, t: "Packing Aman", d: "Rapi & higienis" },
        ].map((b, i) => (
          <div key={i} className="bg-white rounded-xl border border-blush-soft p-3 text-center">
            <b.icon className="w-5 h-5 text-brand mx-auto" />
            <div className="text-[10px] font-semibold mt-1.5">{b.t}</div>
            <div className="text-[9px] text-cocoa-muted">{b.d}</div>
          </div>
        ))}
      </div>

      {/* Voucher Spesial */}
      {vouchers.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-sm">Voucher Spesial Untuk Kamu</h2>
            <span className="text-[11px] text-brand font-medium">Lihat semua ›</span>
          </div>
          <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4">
            {vouchers.map((v) => (
              <div key={v.id} data-testid={`voucher-${v.code}`} className="flex-shrink-0 w-44 bg-gradient-to-br from-[#C4707F] to-[#A85B6A] rounded-2xl p-3.5 text-white relative overflow-hidden">
                <div className="absolute -right-3 -bottom-3 w-16 h-16 rounded-full bg-white/10" />
                <div className="text-2xl font-bold">{v.percent}%<span className="text-sm font-semibold"> OFF</span></div>
                <div className="text-[10px] opacity-90">Min. belanja {Math.round(v.min_purchase / 1000)}K</div>
                <div className="mt-2 text-[10px] bg-white/20 rounded-full px-2 py-1 inline-block font-mono">KODE: {v.code}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Best Seller */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-sm">Best Seller</h2>
          <Link to="/store/categories?sort=bestseller" className="text-[11px] text-brand font-medium">Lihat semua ›</Link>
        </div>
        <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4">
          {bestSeller.map((p) => <div key={p.id} className="w-44 flex-shrink-0"><ProductCard p={p} /></div>)}
        </div>
      </div>

      {/* Rekomendasi grid */}
      <div>
        <h2 className="font-semibold text-sm mb-3">Rekomendasi Untukmu</h2>
        <div className="grid grid-cols-2 gap-3">
          {products.slice(0, 8).map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </div>
    </div>
  );
}
